import { useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, X, Check, Lightning, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

function safeRedirectUri(redirectUri) {
  if (!redirectUri) return null;
  try {
    const u = new URL(decodeURIComponent(redirectUri));
    if (u.protocol !== 'https:' && !(u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1'))) {
      return null;
    }
    return u.toString();
  } catch {
    return null;
  }
}

function buildQuery(baseUrl, params) {
  const u = new URL(baseUrl);
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v);
  }
  return u.toString();
}

export default function OAuthConsentPage() {
  const [searchParams] = useSearchParams();

  const authParams = useMemo(() => {
    const get = (k) => searchParams.get(k) || '';
    return {
      responseType: get('response_type') || 'code',
      clientId: get('client_id'),
      redirectUriRaw: get('redirect_uri'),
      scope: get('scope'),
      state: get('state'),
      codeChallenge: get('code_challenge'),
      codeChallengeMethod: get('code_challenge_method'),
    };
  }, [searchParams]);

  const redirectUri = useMemo(
    () => safeRedirectUri(authParams.redirectUriRaw),
    [authParams.redirectUriRaw]
  );

  const scopes = useMemo(() => {
    if (!authParams.scope) return ['Basic profile (default)'];
    return authParams.scope.split(/[\s+]+/).filter(Boolean);
  }, [authParams.scope]);

  const deny = useCallback(() => {
    if (!redirectUri) return;
    const url = buildQuery(redirectUri, {
      error: 'access_denied',
      error_description: 'The user denied the authorization request',
      state: authParams.state || undefined,
    });
    window.location.replace(url);
  }, [redirectUri, authParams.state]);

  const allow = useCallback(() => {
    if (!redirectUri) return;
    const previewCode = `lf_preview_${crypto.randomUUID().replace(/-/g, '')}`;
    const q = {
      code: previewCode,
      state: authParams.state || undefined,
    };
    if (authParams.codeChallenge) {
      q.code_challenge = authParams.codeChallenge;
      q.code_challenge_method = authParams.codeChallengeMethod || 'S256';
    }
    window.location.replace(buildQuery(redirectUri, q));
  }, [redirectUri, authParams.state, authParams.codeChallenge, authParams.codeChallengeMethod]);

  const missingRedirect = !authParams.redirectUriRaw;
  const invalidRedirect = authParams.redirectUriRaw && !redirectUri;

  return (
    <div className="min-h-screen gradient-aurora flex flex-col">
      <header className="border-b border-border bg-background/70 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl gradient-primary shadow-glow">
              <Lightning size={14} weight="fill" className="text-white" />
            </span>
            <span className="font-display text-xl font-bold tracking-tight text-foreground">LinkFlow</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md surface-card shadow-glow p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="grid place-items-center h-12 w-12 rounded-2xl bg-primary/10 text-primary shrink-0">
              <ShieldCheck size={22} weight="fill" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Authorisation request
              </p>
              <h1 className="font-display text-2xl font-extrabold text-foreground mt-1">
                Authorise access
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                An application is requesting permission to act on your behalf. Review the details before continuing.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-muted/40 p-4 mb-6 text-sm space-y-3">
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                Client
              </span>
              <p className="font-mono text-xs text-foreground break-all mt-1">
                {authParams.clientId || '(not specified)'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                  Response type
                </span>
                <p className="font-mono text-xs text-foreground mt-1">{authParams.responseType}</p>
              </div>
              {authParams.codeChallengeMethod && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                    PKCE
                  </span>
                  <p className="font-mono text-xs text-foreground mt-1">{authParams.codeChallengeMethod}</p>
                </div>
              )}
            </div>
            {redirectUri && (
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                  Return URL
                </span>
                <p className="font-mono text-[11px] text-foreground break-all mt-1">{redirectUri}</p>
              </div>
            )}
          </div>

          <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-3">
            Requested permissions
          </p>
          <ul className="space-y-2 mb-7">
            {scopes.map((s) => (
              <li key={s} className="flex items-center gap-2.5 text-sm text-foreground">
                <span className="grid place-items-center h-5 w-5 rounded-full bg-primary/10 text-primary shrink-0">
                  <Check size={11} weight="bold" />
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ul>

          {missingRedirect && (
            <p className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
              No <code className="text-xs font-mono">redirect_uri</code> was provided. Add OAuth query parameters to this URL for a full flow.
            </p>
          )}
          {invalidRedirect && (
            <p className="flex gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
              The <code className="text-xs font-mono">redirect_uri</code> must be a valid https URL (or http://localhost for development).
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-xl border-border h-11"
              onClick={deny}
              disabled={!redirectUri}
            >
              <X size={16} className="mr-2" />
              Deny
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-xl gradient-primary text-white h-11 shadow-glow hover:opacity-95"
              onClick={allow}
              disabled={!redirectUri}
            >
              <Check size={16} className="mr-2" weight="bold" />
              Allow
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            If you did not initiate this request, choose <strong>Deny</strong>. For LinkFlow sign-in with Google, use{' '}
            <Link to="/login" className="text-primary font-medium underline-offset-2 hover:underline">
              the login page
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
